import type { JSX } from 'react';
import { useState } from 'react';

import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE, ICON_SAVE } from 'Config/Icons';
import { TPRM_ASSESSMENT_DELEGATES, TPRM_ASSESSMENT_OWNER, UCRM_ASSESSMENT_DELEGATES, UCRM_ASSESSMENT_OWNER, UCRM_QUESTIONNAIRE_RESPONDENTS } from 'Config/Tooltips';
import { iso8601ToJsDate, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getDueDiligenceModuleReadWriteRole } from 'Helpers/UserUtils';
import { UpdateDueDiligenceAssessmentAssignmentsRequest } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { getQuestionnaireRespondentIds } from 'Models/Ucrm';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';
import { UserResponse } from 'Models/User';

import styles from './UpdateAssignmentsModal.module.css';

export interface UpdateAssignmentsModalProps {
    assessment: TprmOrUcrmDueDiligenceAssessment;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    hideModal: () => void;
    module: DueDiligenceModule;
    onSuccess: (updatedAssessment: TprmOrUcrmDueDiligenceAssessment) => void;
}

/** Allows the user to update assigned users and due dates for a due diligence questionnaire/assessment. For UCRM, this includes the users who answer the questionnaire. */
export const UpdateAssignmentsModal = (props: UpdateAssignmentsModalProps): JSX.Element => {
    const cachedData = useCachedData();

    const initialOwner = cachedData.users.find((user) => user.cognito_subject === props.assessment.owner_id);
    const initialDelegates = cachedData.users.filter((user) => props.assessment.delegate_ids.includes(user.cognito_subject));
    const initialQuestionnaireRespondents = cachedData.users.filter((user) => getQuestionnaireRespondentIds(props.module, props.assessment).includes(user.cognito_subject));
    const isUcrm = props.module === ProductModule.UCRM;
    const assigneeRolesRestriction = [getDueDiligenceModuleReadWriteRole(props.module)];

    const [selectedOwner, setSelectedOwner] = useState<UserResponse | undefined>(initialOwner);
    const [selectedDelegates, setSelectedDelegates] = useState<UserResponse[]>(initialDelegates);
    const [selectedQuestionnaireRespondents, setSelectedQuestionnaireRespondents] = useState<UserResponse[]>(initialQuestionnaireRespondents);
    const [selectedAssessmentDueDate, setSelectedAssessmentDueDate] = useState<Date | undefined>(props.assessment.assessment_due_date ? iso8601ToJsDate(props.assessment.assessment_due_date) : undefined);
    const [selectedQuestionnaireDueDate, setSelectedQuestionnaireDueDate] = useState<Date | undefined>(props.assessment.questionnaire_due_date ? iso8601ToJsDate(props.assessment.questionnaire_due_date) : undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alertState, setAlertState] = useState<{ type: 'success' | 'error'; message: string }>();

    const handleSubmit = async () => {
        if (!selectedOwner) {
            return;
        }

        try {
            setIsSubmitting(true);
            setAlertState(undefined);

            const request: UpdateDueDiligenceAssessmentAssignmentsRequest = {
                owner_id: selectedOwner.cognito_subject,
                delegate_ids: selectedDelegates.map((user) => user.cognito_subject),
                assessment_due_date: selectedAssessmentDueDate ? jsDateToIso8601(selectedAssessmentDueDate) : undefined,
                questionnaire_due_date: selectedQuestionnaireDueDate ? jsDateToIso8601(selectedQuestionnaireDueDate) : undefined,
                ...(isUcrm && { questionnaire_respondent_ids: selectedQuestionnaireRespondents.map((user) => user.cognito_subject) }),
            };

            const response = await props.assessmentApi.updateAssessmentAssignments(props.assessment.third_party_id, props.assessment.service_id, props.assessment.id, request);
            props.onSuccess(response.data);
            setAlertState({ type: 'success', message: 'Assignments updated.' });
        } catch (error) {
            setAlertState({ type: 'error', message: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open header="Update Assignments">
            {alertState && <Alert type={alertState.type}>{alertState.message}</Alert>}
            <div className={styles.formFieldsColumn}>
                <FormFieldUserSelect formFieldId="assessment-owner" formFieldLabel="Owner" rolesRestriction={assigneeRolesRestriction} notClearable required selectedUser={selectedOwner} onUserSelected={(user) => setSelectedOwner(user)} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_OWNER : TPRM_ASSESSMENT_OWNER} />
                <FormFieldUserMultiSelect formFieldId="assessment-delegates" formFieldLabel="Delegates" rolesRestriction={assigneeRolesRestriction} selectedUsers={selectedDelegates} onUsersSelected={(users) => setSelectedDelegates(users ?? [])} tooltip={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_DELEGATES : TPRM_ASSESSMENT_DELEGATES} />
                {isUcrm && <FormFieldUserMultiSelect formFieldId="questionnaire-respondents" formFieldLabel="Questionnaire Respondents" rolesRestriction={[Role.UCRM_USER]} selectedUsers={selectedQuestionnaireRespondents} onUsersSelected={(users) => setSelectedQuestionnaireRespondents(users ?? [])} tooltip={UCRM_QUESTIONNAIRE_RESPONDENTS} />}
                <FormFieldDatePicker formFieldId="questionnaire-due-date" formFieldLabel="Questionnaire Due Date" dateFormat="MM/dd/yyyy" selected={selectedQuestionnaireDueDate} handleChange={(date: Date | null) => setSelectedQuestionnaireDueDate(date ?? undefined)} />
                <FormFieldDatePicker formFieldId="assessment-due-date" formFieldLabel="Assessment Due Date" dateFormat="MM/dd/yyyy" selected={selectedAssessmentDueDate} handleChange={(date: Date | null) => setSelectedAssessmentDueDate(date ?? undefined)} />
                <div className={styles.buttonContainer}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} fontAwesomeImage={ICON_SAVE} isLoading={isSubmitting} loadingText="Saving..." disabled={!selectedOwner}>
                        Save
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
